import { useState } from 'react';
import { submitContact } from '../../services/contactService';
import { getErrorMessage } from '../../api/client';
import './Contact.css';

const initialFormState = { name: '', email: '', phone: '', subject: '', message: '' };

const Contact = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitError('');

    try {
      await submitContact(formData);
      setSubmitMessage('Thank you! Your message has been sent. We will get back to you soon.');
      setFormData(initialFormState);
    } catch (error) {
      setSubmitError(getErrorMessage(error, 'Unable to send your message. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="container-fluid px-4 py-5">
        {/* Hero Section */}
        <section className="hero-section text-center mb-5">
          <span className="eyebrow">Contact Us</span>
          <h1 className="display-4 fw-bold mb-3">Get in Touch</h1>
          <p className="lead fs-5">
            We'd love to hear from you. Reach out using the information below.
          </p>
        </section>

        {/* Contact Form + Information */}
        <section className="contact-info-section mb-5">
          <div className="row w-100 g-4">
            <div className="col-lg-6">
              <div className="contact-form-wrapper">
                <h2 className="section-title mb-4">Send us a Message</h2>
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="contact-name" className="form-label">Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="contact-email" className="form-label">Email <span className="text-danger">*</span></label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="contact-phone" className="form-label">Phone</label>
                    <input
                      type="tel"
                      id="contact-phone"
                      name="phone"
                      className="form-control"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="contact-subject" className="form-label">Subject <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      className="form-control"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="contact-message" className="form-label">Message <span className="text-danger">*</span></label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows="5"
                      className="form-control"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {submitMessage && <div className="submission-message success">{submitMessage}</div>}
                  {submitError && <div className="submission-message error">{submitError}</div>}

                  <button type="submit" className="btn btn-submit w-100" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="contact-info-wrapper">
                <h2 className="section-title mb-4">Contact Information</h2>

                {/* Office */}
                <div className="contact-item mb-4">
                  <div className="contact-icon">📍</div>
                  <div>
                    <h4 className="fw-bold mb-2">Office Address</h4>
                    <p>SRK Production Headquarters<br />
                    Mumbai, Maharashtra</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="contact-item mb-4">
                  <div className="contact-icon">📞</div>
                  <div>
                    <h4 className="fw-bold mb-2">Phone</h4>
                    <p><a href="tel:+919325111133">+91 9325111133</a></p>
                  </div>
                </div>

                {/* Email */}
                <div className="contact-item mb-4">
                  <div className="contact-icon">✉️</div>
                  <div>
                    <h4 className="fw-bold mb-2">Email</h4>
                    <p><a href="mailto:srkproduction77@gmail.com">srkproduction77@gmail.com</a></p>
                  </div>
                </div>

                {/* Hours */}
                <div className="contact-item">
                  <div className="contact-icon">🕐</div>
                  <div>
                    <h4 className="fw-bold mb-2">Business Hours</h4>
                    <p>Monday - Friday: 9:00 AM - 6:00 PM<br /></p>
                  </div>
                </div>

                {/* Social Links */}
                <div className="social-links">
                  <h4 className="fw-bold mb-3">Follow Us</h4>
                  <div className="d-flex gap-3">
                    <a href="https://www.facebook.com/share/1HRb4Cuh13/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Facebook">f</a>
                    <a href="https://www.instagram.com/srk_production_?igsh=cmM3cnI2eTJiZmZr" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">📷</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section mt-5 pt-5">
          <h2 className="section-title text-center mb-5">Frequently Asked Questions</h2>
          <div className="row">
            <div className="col-lg-6 mb-4">
              <div className="faq-item">
                <h4 className="fw-bold mb-3">How quickly will I receive a response?</h4>
                <p>We aim to respond to all inquiries within 24-48 business hours. During weekends, responses may take longer.</p>
              </div>
            </div>
            <div className="col-lg-6 mb-4">
              <div className="faq-item">
                <h4 className="fw-bold mb-3">Do you offer event sponsorships?</h4>
                <p>Yes! We have various sponsorship packages available. Please contact us directly to discuss your requirements.</p>
              </div>
            </div>
            <div className="col-lg-6 mb-4">
              <div className="faq-item">
                <h4 className="fw-bold mb-3">Can I collaborate with SRK Production?</h4>
                <p>Absolutely! We're always open to collaboration opportunities. Reach out through the contact information above to discuss potential projects.</p>
              </div>
            </div>
            <div className="col-lg-6 mb-4">
              <div className="faq-item">
                <h4 className="fw-bold mb-3">What's the best way to reach you?</h4>
                <p>You can reach us via phone, email, or by visiting our office during business hours. We respond promptly to all inquiries.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;
